import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import VideoFileRoundedIcon from '@mui/icons-material/VideoFileRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import PortalVoiceCallButton from 'src/mentor/components/voice/PortalVoiceCallButton'
import {
  getSupportAgentContactCapabilities,
  getSupportAgentWhatsappThread,
  getSupportAgentWhatsappThreads,
  prepareSupportAgentEmail,
  sendSupportAgentWhatsappAttachment,
  sendSupportAgentWhatsappMessage,
  sendSupportAgentWhatsappTemplate,
} from 'src/supportPortal/services/supportAgentContactService'

function formatThreadTimestamp(value) {
  if (!value) {
    return ''
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function getTemplateActionFriendlyLabel(actionType, fallbackLabel) {
  if (fallbackLabel) {
    return fallbackLabel
  }
  if (actionType === 'permission-template') {
    return 'Conversation opener'
  }
  if (actionType === 'call-heads-up') {
    return 'Call heads-up'
  }
  if (actionType === 'inbound-reply') {
    return 'Reply'
  }
  if (actionType === 'free-form') {
    return 'Message'
  }
  return 'Interaction'
}

function getTemplateActionChipColor(actionType) {
  if (actionType === 'call-heads-up') {
    return 'warning'
  }
  if (actionType === 'permission-template') {
    return 'info'
  }
  if (actionType === 'inbound-reply') {
    return 'secondary'
  }
  return 'default'
}

function getDeliveryStatusMeta(status) {
  const normalized = `${status || ''}`.toLowerCase()
  if (normalized === 'read') {
    return {
      label: 'Read',
      color: '#2563eb',
      icon: <DoneAllRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'delivered') {
    return {
      label: 'Delivered',
      color: '#0f766e',
      icon: <DoneAllRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'sent' || normalized === 'queued' || normalized === 'accepted') {
    return {
      label: normalized === 'queued' ? 'Queued' : 'Sent',
      color: '#475569',
      icon: normalized === 'queued'
        ? <ScheduleRoundedIcon sx={{ fontSize: 15 }} />
        : <DoneRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'failed' || normalized === 'undelivered') {
    return {
      label: normalized === 'undelivered' ? 'Undelivered' : 'Failed',
      color: '#dc2626',
      icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />,
    }
  }
  return {
    label: status || 'Sending',
    color: '#475569',
    icon: <ScheduleRoundedIcon sx={{ fontSize: 15 }} />,
  }
}

function getAttachmentIcon(kind) {
  if (kind === 'image') {
    return <ImageRoundedIcon sx={{ fontSize: 18 }} />
  }
  if (kind === 'audio') {
    return <AudiotrackRoundedIcon sx={{ fontSize: 18 }} />
  }
  if (kind === 'video') {
    return <VideoFileRoundedIcon sx={{ fontSize: 18 }} />
  }
  return <DescriptionRoundedIcon sx={{ fontSize: 18 }} />
}

function AttachmentPreview({ attachment }) {
  const fileName = attachment?.fileName || 'Attachment'
  const url = attachment?.previewUrl || attachment?.downloadUrl
  const kind = attachment?.kind || 'file'

  if (!url) {
    return null
  }

  if (kind === 'image') {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noreferrer"
        sx={{
          display: 'block',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(148, 163, 184, 0.18)',
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src={url}
          alt={fileName}
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: 220,
            objectFit: 'cover',
            background: '#e2e8f0',
          }}
        />
      </Box>
    )
  }

  if (kind === 'audio') {
    return (
      <Box sx={{ borderRadius: 2, overflow: 'hidden', background: 'rgba(255,255,255,0.7)' }}>
        <audio controls preload="metadata" src={url} style={{ width: '100%' }} />
      </Box>
    )
  }

  if (kind === 'video') {
    return (
      <Box sx={{ borderRadius: 2, overflow: 'hidden', background: '#0f172a' }}>
        <video controls preload="metadata" src={url} style={{ width: '100%', maxHeight: 240 }} />
      </Box>
    )
  }

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noreferrer"
      variant="outlined"
      size="small"
      startIcon={getAttachmentIcon(kind)}
      endIcon={<OpenInNewRoundedIcon />}
      sx={{
        alignSelf: 'flex-start',
        justifyContent: 'flex-start',
        borderRadius: 2,
        textTransform: 'none',
      }}
    >
      {fileName}
    </Button>
  )
}

function MessageBubble({ item }) {
  const incoming = item?.direction === 'incoming'
  const attachments = item?.attachments || []
  const deliveryMeta = !incoming ? getDeliveryStatusMeta(item?.deliveryStatus) : null

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: incoming ? 'flex-start' : 'flex-end',
      }}
    >
      <Box
        sx={{
          maxWidth: '92%',
          px: 1.5,
          py: 1.2,
          borderRadius: 2.5,
          background: incoming
            ? 'rgba(241, 245, 249, 0.96)'
            : 'linear-gradient(135deg, rgba(220, 252, 231, 0.98) 0%, rgba(209, 250, 229, 0.95) 100%)',
          border: incoming
            ? '1px solid rgba(148, 163, 184, 0.18)'
            : '1px solid rgba(22, 163, 74, 0.18)',
          boxShadow: incoming
            ? '0 8px 24px rgba(15, 23, 42, 0.05)'
            : '0 10px 26px rgba(22, 163, 74, 0.08)',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.75, flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {incoming ? item?.targetDisplayName || 'Recipient' : item?.supportAgentName || 'Support'}
            </Typography>
            <Chip
              size="small"
              label={getTemplateActionFriendlyLabel(item?.actionType, item?.label)}
              color={getTemplateActionChipColor(item?.actionType)}
              variant="outlined"
              sx={{ height: 22 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatThreadTimestamp(item?.createdAt)}
          </Typography>
        </Stack>

        {item?.messagePreview ? (
          <Typography
            variant="body2"
            sx={{
              color: '#111827',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.55,
            }}
          >
            {item.messagePreview}
          </Typography>
        ) : null}

        {attachments.length > 0 ? (
          <Stack spacing={1} sx={{ mt: item?.messagePreview ? 1.1 : 0.25 }}>
            {attachments.map((attachment, index) => (
              <AttachmentPreview
                key={`${attachment?.fileId || attachment?.downloadUrl || 'attachment'}-${index}`}
                attachment={attachment}
              />
            ))}
          </Stack>
        ) : null}

        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
          {attachments.length > 0 ? (
            <Chip
              size="small"
              icon={<AttachFileRoundedIcon sx={{ fontSize: 14 }} />}
              label={`${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`}
              variant="outlined"
              sx={{ height: 22 }}
            />
          ) : null}
          {deliveryMeta ? (
            <Stack direction="row" spacing={0.4} alignItems="center" sx={{ color: deliveryMeta.color }}>
              {deliveryMeta.icon}
              <Typography variant="caption" sx={{ color: deliveryMeta.color, fontWeight: 700 }}>
                {deliveryMeta.label}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Box>
    </Box>
  )
}

function buildContextFromThread(thread) {
  if (!thread) {
    return null
  }

  return {
    patientId: thread?.patientId || undefined,
    practitionerId: thread?.practitionerId || undefined,
    bookedEventId: thread?.bookedEventId || undefined,
    clinicBrandId: thread?.clinicBrandId || undefined,
    clinicId: thread?.clinicId || undefined,
    targetRole: thread?.targetRole || undefined,
    eventType: thread?.eventType || undefined,
  }
}

export default function SupportWhatsappThreadsPanel({
  uiSurface = 'support-floating-threads',
  sx = {},
}) {
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [threadsError, setThreadsError] = useState('')
  const [threads, setThreads] = useState([])
  const [selectedThreadId, setSelectedThreadId] = useState('')
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState('')
  const [threadState, setThreadState] = useState({
    messages: [],
    templateActions: [],
    freeFormAllowed: false,
    targetDisplayName: '',
  })
  const [capabilities, setCapabilities] = useState(null)
  const [selectedTemplateAction, setSelectedTemplateAction] = useState('')
  const [messageDraft, setMessageDraft] = useState('')
  const [messageSending, setMessageSending] = useState(false)
  const [templateSending, setTemplateSending] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [attachmentFiles, setAttachmentFiles] = useState([])
  const [attachmentInputKey, setAttachmentInputKey] = useState(0)

  const selectedThread = useMemo(
    () => threads.find((item) => item?.threadId === selectedThreadId) || null,
    [selectedThreadId, threads],
  )
  const selectedContext = useMemo(() => buildContextFromThread(selectedThread), [selectedThread])

  function resetAttachmentPicker() {
    setAttachmentFiles([])
    setAttachmentInputKey((currentValue) => currentValue + 1)
  }

  async function refreshThreads(preferredThreadId) {
    setThreadsLoading(true)
    const result = await getSupportAgentWhatsappThreads()
    setThreadsLoading(false)

    if (!result?.ok) {
      setThreads([])
      setThreadsError(result?.message || 'Unable to load support WhatsApp threads.')
      setSelectedThreadId('')
      return null
    }

    const nextThreads = result?.threads || []
    setThreads(nextThreads)
    setThreadsError('')
    setSelectedThreadId((currentValue) => {
      const requestedThreadId = preferredThreadId || currentValue
      if (requestedThreadId && nextThreads.some((item) => item?.threadId === requestedThreadId)) {
        return requestedThreadId
      }
      return nextThreads[0]?.threadId || ''
    })
    return nextThreads
  }

  useEffect(() => {
    refreshThreads()
  }, [])

  useEffect(() => {
    let active = true

    async function loadThreadDetails() {
      if (!selectedContext) {
        setCapabilities(null)
        setThreadState({
          messages: [],
          templateActions: [],
          freeFormAllowed: false,
          targetDisplayName: '',
        })
        setDetailsError('')
        resetAttachmentPicker()
        return
      }

      setDetailsLoading(true)
      const [capabilityResult, threadResult] = await Promise.all([
        getSupportAgentContactCapabilities(selectedContext),
        getSupportAgentWhatsappThread(selectedContext),
      ])

      if (!active) {
        return
      }

      if (capabilityResult?.ok) {
        setCapabilities(capabilityResult)
      } else {
        setCapabilities(null)
      }

      if (threadResult?.ok) {
        setThreadState({
          messages: threadResult?.messages || [],
          templateActions: threadResult?.templateActions || [],
          freeFormAllowed: !!threadResult?.freeFormAllowed,
          targetDisplayName:
            threadResult?.targetDisplayName || capabilityResult?.targetDisplayName || selectedThread?.targetDisplayName || '',
        })
        setDetailsError('')
        setSelectedTemplateAction((currentValue) => {
          const nextTemplateIds = (threadResult?.templateActions || []).map((item) => item?.id)
          if (currentValue && nextTemplateIds.includes(currentValue)) {
            return currentValue
          }
          return threadResult?.templateActions?.[0]?.id || ''
        })
      } else {
        setThreadState({
          messages: [],
          templateActions: [],
          freeFormAllowed: false,
          targetDisplayName: selectedThread?.targetDisplayName || '',
        })
        setDetailsError(threadResult?.message || 'Unable to load this WhatsApp conversation.')
      }
      setDetailsLoading(false)
    }

    loadThreadDetails()

    return () => {
      active = false
    }
  }, [selectedContext, selectedThread?.targetDisplayName])

  async function handleSendTemplate() {
    if (!selectedContext || !selectedTemplateAction) {
      toast.error('Choose a conversation and template action first.')
      return
    }

    setTemplateSending(true)
    const result = await sendSupportAgentWhatsappTemplate(selectedContext, selectedTemplateAction)
    setTemplateSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp template.')
      return
    }

    toast.success('Template sent on WhatsApp.')
    await refreshThreads(selectedThreadId)
  }

  async function handleSendMessage() {
    if (!selectedContext || (!messageDraft.trim() && attachmentFiles.length === 0)) {
      toast.error('Enter a message or attach a file first.')
      return
    }

    setMessageSending(true)
    const result = attachmentFiles.length > 0
      ? await sendSupportAgentWhatsappAttachment(selectedContext, messageDraft.trim(), attachmentFiles)
      : await sendSupportAgentWhatsappMessage(selectedContext, messageDraft.trim())
    setMessageSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp message.')
      return
    }

    toast.success(attachmentFiles.length > 0 ? 'WhatsApp attachment sent.' : 'WhatsApp message sent.')
    setMessageDraft('')
    resetAttachmentPicker()
    await refreshThreads(selectedThreadId)
  }

  async function handlePrepareEmail() {
    if (!selectedContext) {
      return
    }

    setEmailLoading(true)
    const result = await prepareSupportAgentEmail(selectedContext)
    setEmailLoading(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to prepare the email draft.')
      return
    }

    if (!result?.data?.mailtoUri) {
      toast.error('Email draft URL was not returned.')
      return
    }

    window.location.href = result.data.mailtoUri
  }

  function handleAttachmentSelection(event) {
    const nextFiles = Array.from(event?.target?.files || [])
    if (nextFiles.length === 0) {
      return
    }
    setAttachmentFiles((currentValue) => [...currentValue, ...nextFiles])
  }

  function removeAttachmentAt(indexToRemove) {
    setAttachmentFiles((currentValue) => currentValue.filter((_, index) => index !== indexToRemove))
  }

  const channels = capabilities?.channels || {}
  const phoneChannel = channels?.phone || {}
  const emailChannel = channels?.email || {}
  const canCall = !!phoneChannel?.enabled
  const canEmail = !!emailChannel?.enabled
  const sendDisabled = !threadState?.freeFormAllowed || (!messageDraft.trim() && attachmentFiles.length === 0) || messageSending

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        background: '#f8fafc',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Support Threads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Continue support WhatsApp conversations here with delivery status, media, and call tools in one place.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={threadsLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshRoundedIcon />}
          onClick={() => refreshThreads(selectedThreadId)}
          disabled={threadsLoading}
        >
          Refresh
        </Button>
      </Stack>

      {threadsError ? <Alert severity="warning">{threadsError}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid rgba(226, 232, 240, 1)',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1,
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(226, 232, 240, 1)', background: '#ffffff' }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              icon={<HistoryEduRoundedIcon />}
              label={`${threads.length} active thread${threads.length === 1 ? '' : 's'}`}
              color="primary"
              variant="outlined"
            />
            {selectedThread?.freeFormAllowed ? (
              <Chip size="small" color="success" label="Free-form open" />
            ) : null}
          </Stack>
        </Box>

        <Box
          sx={{
            px: 1.25,
            py: 1.25,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflowY: 'auto',
            maxHeight: 220,
            background: 'rgba(248, 250, 252, 0.92)',
          }}
        >
          {threadsLoading && threads.length === 0 ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : null}

          {!threadsLoading && threads.length === 0 ? (
            <Alert severity="info">
              Start a WhatsApp conversation from a patient or practitioner record and it will show up here.
            </Alert>
          ) : null}

          {threads.map((thread) => {
            const selected = thread?.threadId === selectedThreadId
            const deliveryMeta = thread?.lastDirection !== 'incoming'
              ? getDeliveryStatusMeta(thread?.lastDeliveryStatus || thread?.lastStatus)
              : null
            return (
              <Button
                key={thread?.threadId}
                variant={selected ? 'contained' : 'outlined'}
                color={selected ? 'primary' : 'inherit'}
                onClick={() => setSelectedThreadId(thread?.threadId)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderRadius: 2.5,
                  px: 1.5,
                  py: 1.25,
                  alignItems: 'flex-start',
                  background: selected
                    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    : '#ffffff',
                  color: selected ? '#ffffff' : '#0f172a',
                  borderColor: selected ? 'transparent' : 'rgba(226, 232, 240, 1)',
                }}
              >
                <Stack spacing={0.65} sx={{ width: '100%', textAlign: 'left' }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {thread?.targetDisplayName || 'Recipient'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: selected ? 'rgba(255,255,255,0.82)' : 'text.secondary' }}
                    >
                      {formatThreadTimestamp(thread?.lastCreatedAt)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {thread?.eventType ? (
                      <Chip
                        size="small"
                        label={thread.eventType}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{
                          height: 22,
                          color: selected ? '#0f172a' : undefined,
                          background: selected ? 'rgba(255,255,255,0.92)' : undefined,
                        }}
                      />
                    ) : null}
                    <Chip
                      size="small"
                      label={getTemplateActionFriendlyLabel(thread?.lastActionType, thread?.lastActionLabel)}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{
                        height: 22,
                        color: selected ? '#0f172a' : undefined,
                        background: selected ? 'rgba(255,255,255,0.92)' : undefined,
                      }}
                    />
                    {thread?.attachmentCount ? (
                      <Chip
                        size="small"
                        icon={<AttachFileRoundedIcon sx={{ fontSize: 14 }} />}
                        label={`${thread.attachmentCount}`}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{
                          height: 22,
                          color: selected ? '#0f172a' : undefined,
                          background: selected ? 'rgba(255,255,255,0.92)' : undefined,
                        }}
                      />
                    ) : null}
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{
                      color: selected ? 'rgba(255,255,255,0.92)' : '#475569',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {thread?.lastMessagePreview || 'No message preview available yet.'}
                  </Typography>
                  {deliveryMeta ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: selected ? '#dbeafe' : deliveryMeta.color }}>
                      {deliveryMeta.icon}
                      <Typography variant="caption" sx={{ color: selected ? '#dbeafe' : deliveryMeta.color, fontWeight: 700 }}>
                        {deliveryMeta.label}
                      </Typography>
                    </Stack>
                  ) : null}
                </Stack>
              </Button>
            )
          })}
        </Box>

        <Divider />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
          }}
        >
          {selectedThread ? (
            <>
              <Box sx={{ px: 2, py: 1.75, borderBottom: '1px solid rgba(226, 232, 240, 1)' }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        {threadState?.targetDisplayName || selectedThread?.targetDisplayName || 'Recipient'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedThread?.targetRole || 'contact'}
                        {selectedThread?.practitionerName ? ` • ${selectedThread.practitionerName}` : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                      {canCall ? (
                        <PortalVoiceCallButton
                          actorRole="support-agent"
                          patientId={selectedContext?.patientId}
                          practitionerId={selectedContext?.practitionerId}
                          bookedEventId={selectedContext?.bookedEventId}
                          clinicBrandId={selectedContext?.clinicBrandId}
                          clinicId={selectedContext?.clinicId}
                          targetRole={selectedContext?.targetRole}
                          eventType={selectedContext?.eventType}
                          uiSurface={uiSurface}
                          allowedDestinationTypes={[phoneChannel?.destinationType]}
                          autoStartWhenSingleRoute
                          buttonLabel="Call"
                          size="small"
                        />
                      ) : null}
                      {canEmail ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={emailLoading ? <CircularProgress size={14} color="inherit" /> : <EmailRoundedIcon />}
                          disabled={emailLoading}
                          onClick={handlePrepareEmail}
                        >
                          Email
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {selectedThread?.eventType ? (
                      <Chip size="small" label={selectedThread.eventType} variant="outlined" />
                    ) : null}
                    {threadState?.freeFormAllowed ? (
                      <Chip size="small" color="success" label="Free-form open" />
                    ) : (
                      <Chip size="small" color="warning" label="Template first" />
                    )}
                  </Stack>
                </Stack>
              </Box>

              {detailsError ? <Alert severity="warning" sx={{ m: 1.5 }}>{detailsError}</Alert> : null}

              {detailsLoading ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={26} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      px: 2,
                      py: 1.75,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                      background:
                        'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(255, 255, 255, 1) 18%)',
                    }}
                  >
                    {threadState?.messages?.length > 0 ? (
                      threadState.messages.map((item) => <MessageBubble key={item?.id} item={item} />)
                    ) : (
                      <Alert severity="info">
                        No messages are recorded for this thread yet.
                      </Alert>
                    )}
                  </Box>

                  <Divider />

                  <Box sx={{ px: 2, py: 1.75 }}>
                    {!threadState?.freeFormAllowed ? (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        Start with an approved template. Free-form chat opens after the recipient replies within the 24-hour window.
                      </Alert>
                    ) : (
                      <Alert severity="success" sx={{ mb: 1.5 }}>
                        The recipient has replied recently, so you can continue with free-form WhatsApp messages and attachments here.
                      </Alert>
                    )}

                    {threadState?.templateActions?.length > 0 ? (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Approved message"
                          value={selectedTemplateAction}
                          onChange={(event) => setSelectedTemplateAction(event.target.value)}
                        >
                          {threadState.templateActions.map((action) => (
                            <MenuItem key={action?.id} value={action?.id}>
                              {action?.label || action?.id}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button
                          variant="outlined"
                          color="success"
                          startIcon={templateSending ? <CircularProgress size={14} color="inherit" /> : <WhatsAppIcon />}
                          onClick={handleSendTemplate}
                          disabled={!selectedTemplateAction || templateSending}
                          sx={{ minWidth: 132 }}
                        >
                          Send
                        </Button>
                      </Stack>
                    ) : null}

                    {attachmentFiles.length > 0 ? (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
                        {attachmentFiles.map((file, index) => (
                          <Chip
                            key={`${file?.name || 'file'}-${index}`}
                            icon={<AttachFileRoundedIcon />}
                            label={file?.name || `Attachment ${index + 1}`}
                            onDelete={() => removeAttachmentAt(index)}
                            deleteIcon={<CloseRoundedIcon />}
                            variant="outlined"
                            sx={{ maxWidth: '100%' }}
                          />
                        ))}
                      </Stack>
                    ) : null}

                    <Stack direction="row" spacing={1} alignItems="flex-end">
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={5}
                        size="small"
                        label="Free-form reply"
                        value={messageDraft}
                        onChange={(event) => setMessageDraft(event.target.value)}
                        disabled={!threadState?.freeFormAllowed || messageSending}
                        helperText={
                          threadState?.freeFormAllowed
                            ? 'Replies are sent in-app as the support agent. You can also attach media or documents.'
                            : 'Send an approved opener first, then continue in free form after the recipient replies.'
                        }
                      />
                      <Stack spacing={0.75} sx={{ pb: 0.25 }}>
                        <Button
                          component="label"
                          variant="outlined"
                          disabled={!threadState?.freeFormAllowed || messageSending}
                          startIcon={<AttachFileRoundedIcon />}
                          sx={{ minWidth: 132, height: 40 }}
                        >
                          Attach
                          <input
                            key={attachmentInputKey}
                            hidden
                            multiple
                            type="file"
                            onChange={handleAttachmentSelection}
                          />
                        </Button>
                        <Button
                          variant="contained"
                          endIcon={messageSending ? <CircularProgress size={14} color="inherit" /> : <SendRoundedIcon />}
                          onClick={handleSendMessage}
                          disabled={sendDisabled}
                          sx={{ minWidth: 132, height: 40 }}
                        >
                          Send
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </>
              )}
            </>
          ) : (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                Choose a support conversation to continue the chat and trigger call or email from the same place.
              </Alert>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  )
}
